import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react';
import clsx from 'clsx';

import { ChevronDownIcon, ChevronRightIcon } from '../internal/icons';
import styles from './Tree.module.css';

export interface TreeNode {
  id: string;
  label: ReactNode;
  /** Plain text used as the item's accessible name when `label` is rich. */
  labelText?: string;
  /** Canonical id / code shown in mono — the redundant non-colour channel. */
  code?: string;
  icon?: ReactNode;
  /** Right-aligned secondary content: a count, a status Badge. */
  meta?: ReactNode;
  children?: readonly TreeNode[];
  disabled?: boolean;
}

export interface TreeProps {
  /** Accessible name for the tree. Required: an unnamed tree is unnavigable. */
  label: string;
  nodes: readonly TreeNode[];
  selectedId?: string;
  onSelect?: (id: string, node: TreeNode) => void;
  /** Controlled expansion. Omit to let the Tree own it. */
  expandedIds?: readonly string[];
  onExpandedChange?: (ids: readonly string[]) => void;
  defaultExpandedIds?: readonly string[];
  dense?: boolean;
  className?: string;
}

interface FlatItem {
  node: TreeNode;
  level: number;
  parentId: string | undefined;
}

const flatten = (
  nodes: readonly TreeNode[],
  expanded: ReadonlySet<string>,
  level: number,
  parentId: string | undefined,
  out: FlatItem[],
): FlatItem[] => {
  for (const node of nodes) {
    out.push({ node, level, parentId });
    if (node.children !== undefined && node.children.length > 0 && expanded.has(node.id)) {
      flatten(node.children, expanded, level + 1, node.id, out);
    }
  }
  return out;
};

const hasChildren = (node: TreeNode): boolean =>
  node.children !== undefined && node.children.length > 0;

/**
 * `role="tree"` with roving tabindex: exactly one item is tabbable, and Up/Down
 * walk the *visible* rows, so collapsing a branch never leaves focus stranded.
 * Left collapses then climbs, Right expands then descends (WAI-ARIA tree pattern).
 */
export function Tree({
  label,
  nodes,
  selectedId,
  onSelect,
  expandedIds,
  onExpandedChange,
  defaultExpandedIds,
  dense = false,
  className,
}: TreeProps) {
  const [uncontrolledExpanded, setUncontrolledExpanded] = useState<readonly string[]>(
    defaultExpandedIds ?? [],
  );
  const expandedList = expandedIds ?? uncontrolledExpanded;
  const expanded = useMemo(() => new Set(expandedList), [expandedList]);

  const items = useMemo(() => flatten(nodes, expanded, 1, undefined, []), [nodes, expanded]);

  const firstId = items[0]?.node.id;
  const [activeId, setActiveId] = useState<string | undefined>(undefined);
  // The roving item: the last one focused, else the selection, else the first row.
  const resolvedActive =
    activeId !== undefined && items.some((item) => item.node.id === activeId)
      ? activeId
      : (selectedId !== undefined && items.some((item) => item.node.id === selectedId)
          ? selectedId
          : firstId);

  const refs = useRef(new Map<string, HTMLLIElement>());

  const setExpandedIds = useCallback(
    (next: readonly string[]) => {
      if (expandedIds === undefined) setUncontrolledExpanded(next);
      onExpandedChange?.(next);
    },
    [expandedIds, onExpandedChange],
  );

  const setExpandedFor = useCallback(
    (id: string, open: boolean) => {
      const current = expandedIds ?? uncontrolledExpanded;
      if (open === current.includes(id)) return;
      setExpandedIds(open ? [...current, id] : current.filter((entry) => entry !== id));
    },
    [expandedIds, uncontrolledExpanded, setExpandedIds],
  );

  const focusItem = useCallback((id: string) => {
    setActiveId(id);
    refs.current.get(id)?.focus();
  }, []);

  const onKeyDown = useCallback(
    (event: KeyboardEvent<HTMLUListElement>) => {
      if (resolvedActive === undefined) return;
      const index = items.findIndex((item) => item.node.id === resolvedActive);
      if (index === -1) return;
      const item = items[index];
      if (item === undefined) return;
      const { node } = item;

      const move = (target: FlatItem | undefined): void => {
        if (target === undefined) return;
        event.preventDefault();
        focusItem(target.node.id);
      };

      switch (event.key) {
        case 'ArrowDown':
          move(items[index + 1]);
          return;
        case 'ArrowUp':
          move(items[index - 1]);
          return;
        case 'Home':
          move(items[0]);
          return;
        case 'End':
          move(items[items.length - 1]);
          return;
        case 'ArrowRight': {
          if (!hasChildren(node)) return;
          event.preventDefault();
          if (expanded.has(node.id)) {
            const child = items[index + 1];
            if (child !== undefined) focusItem(child.node.id);
          } else {
            setExpandedFor(node.id, true);
          }
          return;
        }
        case 'ArrowLeft': {
          event.preventDefault();
          if (hasChildren(node) && expanded.has(node.id)) {
            setExpandedFor(node.id, false);
            return;
          }
          if (item.parentId !== undefined) focusItem(item.parentId);
          return;
        }
        case 'Enter':
        case ' ': {
          event.preventDefault();
          if (node.disabled === true) return;
          if (onSelect !== undefined) {
            onSelect(node.id, node);
            return;
          }
          // No selection handler: Enter/Space toggle the branch instead of
          // doing nothing at all.
          if (hasChildren(node)) setExpandedFor(node.id, !expanded.has(node.id));
          return;
        }
        default:
          return;
      }
    },
    [items, resolvedActive, expanded, focusItem, setExpandedFor, onSelect],
  );

  const renderLevel = (levelNodes: readonly TreeNode[], level: number): ReactNode => (
    <ul
      className={level === 1 ? styles.tree : styles.group}
      role={level === 1 ? 'tree' : 'group'}
      aria-label={level === 1 ? label : undefined}
      onKeyDown={level === 1 ? onKeyDown : undefined}
    >
      {levelNodes.map((node) => {
        const branch = hasChildren(node);
        const open = branch && expanded.has(node.id);
        const isSelected = selectedId !== undefined && selectedId === node.id;
        return (
          <li
            key={node.id}
            ref={(element) => {
              if (element === null) refs.current.delete(node.id);
              else refs.current.set(node.id, element);
            }}
            role="treeitem"
            aria-expanded={branch ? open : undefined}
            aria-selected={onSelect === undefined ? undefined : isSelected}
            aria-disabled={node.disabled === true ? true : undefined}
            aria-label={node.labelText}
            aria-level={level}
            tabIndex={resolvedActive === node.id ? 0 : -1}
            data-id={node.id}
            className={styles.item}
            onFocus={(event) => {
              // Only the row itself owns the roving index, not nested rows.
              if (event.target === event.currentTarget) setActiveId(node.id);
            }}
            onClick={(event) => {
              event.stopPropagation();
              if (node.disabled === true) return;
              focusItem(node.id);
              if (onSelect !== undefined) onSelect(node.id, node);
              else if (branch) setExpandedFor(node.id, !open);
            }}
          >
            <span
              className={clsx(
                styles.row,
                dense && styles.dense,
                isSelected && styles.selected,
                node.disabled === true && styles.disabled,
              )}
            >
              {branch ? (
                <span
                  className={styles.twisty}
                  onClick={(event) => {
                    // Clicking the twisty must not also select the row.
                    event.stopPropagation();
                    focusItem(node.id);
                    setExpandedFor(node.id, !open);
                  }}
                  aria-hidden="true"
                >
                  {open ? <ChevronDownIcon /> : <ChevronRightIcon />}
                </span>
              ) : (
                <span className={styles.twistySpacer} aria-hidden="true" />
              )}
              {node.icon !== undefined && (
                <span className={styles.icon} aria-hidden="true">
                  {node.icon}
                </span>
              )}
              <span className={styles.label}>{node.label}</span>
              {node.code !== undefined && <span className={styles.code}>{node.code}</span>}
              {node.meta !== undefined && <span className={styles.meta}>{node.meta}</span>}
            </span>
            {open && node.children !== undefined && renderLevel(node.children, level + 1)}
          </li>
        );
      })}
    </ul>
  );

  return <div className={clsx(styles.root, className)}>{renderLevel(nodes, 1)}</div>;
}
